import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { useNovosNegociosListaPagination } from "../hooks/novos_negocios_lista/pagination";
import { useNovosNegociosListaFilters } from "../hooks/novos_negocios_lista/filters";
import { NovosNegociosLista } from "../../domain/models/novos_negocios_lista";
import { EmailForm } from "../../../../common/presentation/schemas/email";
import { emailTemplates } from "../../../../common/utils/email_templates";
import { NovosNegocios } from "../../domain/models/novos_negocios";
import { ReportNovosNegociosForm } from "../schemas/reports/novosNegociosReport";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { evaluateParamValue } from "../../../../common/utils/params_utils";

type LoadNovosNegociosListaState = {
    idsCliente?: number[],
    cidade?: string | null,
    data?: Date | null,
    status?: number,

    page?: number;
    rowsPerPage?: number;

}

export const useNovosNegociosViewModel = () => {

    const {
        reports: {
            getReportNovosNegociosListaUseCase,
            getReportNovosNegociosUseCase,

        },
        comercial: {
            getAllNovosNegociosUseCase,
            deleteNovosNegociosUseCase,
            sendEmailNovosNegociosUseCase,

        },
        commons: {
            getClientesDropDownUseCase,
            getCentroCustoDropDownUseCase,
        },

    } = useDependencyInjection();


    const navigate = useNavigate();
    const { pushErrorNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const debouncedRef = useCreateDebounceHandler(1500);

    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    const [selectedIdNovosNegocios, setSelectedIdNovosNegocios] = useState<number>();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [novosNegociosLista, setNovosNegociosLista] = useState<NovosNegociosLista[]>();
    const [ddlCliente, setDdlCliente] = useState<DropDown[]>();
    const [ddlCentroCusto, setDdlCentroCusto] = useState<CentroCustoDropDown[]>();

    const [reportViewerState, startReportViewerProcess] = useReportViewer();
    const [openReportNovosNegociosModal, setOpenReportNovosNegociosModal] = useState(false);

    const filters = useNovosNegociosListaFilters();
    const pagination = useNovosNegociosListaPagination();

    //email
    const [selectedNovosNegocios, setSelectedNovosNegocios] = useState<NovosNegocios>();
    const [openEmailModal, setOpenEmailModal] = useState(false);

    const [emailSendTo, setEmailSendTo] = useState("");
    const [emailSubject, setEmailSubject] = useState("");
    const [emailAttachment, setEmailAttachment] = useState("");
    const [emailBody, setEmailBody] = useState("");

    //#region CARREGAMENTO.

    const loadNovosNegociosLista = useCallback(async (funcState?: LoadNovosNegociosListaState) => {

        try {

            const filteredIdsCliente = evaluateParamValue(funcState?.idsCliente, filters.idsCliente);
            const filteredCidade = evaluateParamValue(funcState?.cidade, filters.cidade);
            const filteredData = evaluateParamValue(funcState?.data, filters.data);
            const filteredStatus = evaluateParamValue(funcState?.status, filters.status);

            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllNovosNegociosUseCase.execute(
                filteredIdsCliente ?? [],
                filteredCidade ?? "",
                filteredData ?? undefined,
                filteredStatus ?? 4,

                filteredPage,
                filteredRowsPerPage,
            );

            setNovosNegociosLista(getAllNovosNegociosUseCase.items);

            pagination.setRowsPerPage(getAllNovosNegociosUseCase.pageSize);
            pagination.setTotalItems(getAllNovosNegociosUseCase.totalItems);
        } catch (e: unknown) {
            setNovosNegociosLista([]);
            pushErrorNotification("Error", processError(e, "Falha ao buscar lista de novos negócios!"));
        }
    }, [
        filters,
        pagination,
        getAllNovosNegociosUseCase,
        pushErrorNotification
    ]);


    const loadClientesDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const response = await getClientesDropDownUseCase.getClientesDropDown();
            setDdlCliente(response);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de clientes!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        getClientesDropDownUseCase,
        setPageLoading,
        pushErrorNotification
    ]);

    const loadCentroCustoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const response = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(response);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de centros de custo!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        getCentroCustoDropDownUseCase,
        setPageLoading,
        pushErrorNotification
    ]);


    //#endregion

    //#region PAGE START.
    usePageStartBootstrap(async () => {
        try {
            if (novosNegociosLista === undefined) {
                await loadNovosNegociosLista();
                await loadClientesDropDown();
                await loadCentroCustoDropDown();
            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de novos negócios!")[0]);
        }
    });
    //#endregion




    //#region NOVO/EDITAR.

    const handleNewNovosNegocios = useCallback(() => {
        navigate("/NovosNegocios_cadastro");
    }, [navigate]);

    const handleEditNovosNegocios = useCallback((idNovosNegocios: number | null) => {
        navigate("/NovosNegocios_cadastro", { state: { idNovosNegocios } });
    }, [navigate]);

    //#endregion



    //#region PAGINAÇÃO.


    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadNovosNegociosLista();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de novos negócios!'));
        } finally {
            setLoading(false);
        }
    }, [loadNovosNegociosLista, pushErrorNotification]);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadNovosNegociosLista({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de novos negócios!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadNovosNegociosLista, pushErrorNotification]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadNovosNegociosLista({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de novos negócios!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadNovosNegociosLista, pushErrorNotification]);
    //#endregion






    //#region DELECAO

    const handleNovosNegociosDeleteOpenState = useCallback((idNovosNegocios: number | null, openState: boolean) => {
        if (openState) {
            if (idNovosNegocios) {
                setSelectedIdNovosNegocios(idNovosNegocios);
                setOpenDeleteModal(true);
            } else {
                setSelectedIdNovosNegocios(undefined);
                setOpenDeleteModal(false);
            }
        }
        setOpenDeleteModal(openState);
    }, []);


    const handleConfirmarDeletarNovosNegocios = useCallback(async () => {
        try {

            setSaving(true);
            if (selectedIdNovosNegocios) {
                await deleteNovosNegociosUseCase.execute(selectedIdNovosNegocios);
                pushInfoNotification("Sucesso", " Novos Negócios deletado com sucesso!", { isAutoFinish: true });
            } else {
                pushErrorNotification("Erro", "Não foi possivel deletar Novos Negócios!", { isAutoFinish: true });

            }

            await loadClientesDropDown()
            await loadNovosNegociosLista()
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Deletar Novos Negócios!"));
        } finally {
            setSaving(false);
            setOpenDeleteModal(false);
            setSelectedIdNovosNegocios(undefined)
        }
    }, [
        selectedIdNovosNegocios,
        deleteNovosNegociosUseCase,
        loadClientesDropDown,
        loadNovosNegociosLista,
        pushErrorNotification,
        pushInfoNotification]);


    //#endregion





    //#region PESQUISA.

    const handleIdsClienteChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setIdsCliente(newValues)
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadNovosNegociosLista({ idsCliente: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar novos negócios!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadNovosNegociosLista,
        pushErrorNotification]
    );

    const handleCidadeChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {

            filters.setCidade(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadNovosNegociosLista({ cidade: newValue ?? undefined, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar novos negócios!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadNovosNegociosLista, pushErrorNotification]
    );


    const handleDataChange = useDebouncedCallback(
        debouncedRef,
        (newValue: Date | null) => {

            filters.setData(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadNovosNegociosLista({ data: newValue ?? undefined, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar novos negócios!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadNovosNegociosLista, pushErrorNotification]
    );

    const handleStatusChange = useDebouncedCallback(
        debouncedRef,
        (newValue: number | null) => {

            filters.setStatus(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadNovosNegociosLista({ status: newValue ?? undefined, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar novos negócios!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadNovosNegociosLista, pushErrorNotification]);


    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadNovosNegociosLista({
                idsCliente: [],
                cidade: null,
                data: null,
                status: 4,
                page: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar novos negócios!'));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        pagination,
        loadNovosNegociosLista,
        pushErrorNotification]);

    //#endregion 

    //#region  EXPORTAR/RELATÓRIO

    const handleOpenReportNovosNegociosModal = useCallback(async (openState: boolean) => {
        setOpenReportNovosNegociosModal(openState);
        try {
            setLoading(true);
            if (openState) {
                await loadCentroCustoDropDown();
                setOpenReportNovosNegociosModal(openState);
            } else {
                setOpenReportNovosNegociosModal(openState);
            }

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao carregar filtros do relatório!'));
        }
        finally {
            setLoading(false);
        }
    }, [
        loadCentroCustoDropDown,
        pushErrorNotification,
    ]);

    const handleNovosNegociosExportar = useCallback(async (idNovosNegocios: number) => {
        try {
            setLoading(true);
            await startReportViewerProcess({
                fetchCallback: async (requiredMimeType) => {
                    return await getReportNovosNegociosUseCase.execute(idNovosNegocios, requiredMimeType);
                }
            });

        } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, 'Falha ao gerar relatório!'));
        } finally {
            setLoading(false);
        }
    }, [
        getReportNovosNegociosUseCase,
        pushErrorNotification,
        startReportViewerProcess
    ]);


    const handleReportNovosNegociosListaSubmit = useCallback(async (form: ReportNovosNegociosForm) => {
        try {
            setLoading(true)
            await startReportViewerProcess({
                fetchCallback: async (mimeType) => {
                    return await getReportNovosNegociosListaUseCase.execute(
                        {
                            centroCusto: form.centroCusto,
                            titulo: form.titulo,
                            status: form.status,
                            dataDe: form.dataDe,
                            dataAte: form.dataAte,
                            colunas: form.colunas,
                            requiredMimeType: mimeType,
                        });
                }
            });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao carregar relatório!'));

        } finally {
            setLoading(false);
        }
    }, [
        getReportNovosNegociosListaUseCase,
        pushErrorNotification,
        startReportViewerProcess
    ]);


    //#endregion



    //#region  EMAIL 

    const handleEmailOpenState = useCallback((idNovosNegocios: number | null, openState: boolean) => {
        if (openState) {
            if (idNovosNegocios && novosNegociosLista) {
                const novosNegocios = novosNegociosLista.find(x => x.idNovosNegocios === idNovosNegocios);
                if (novosNegocios) {
                    setEmailSendTo("");
                    setEmailSubject("Requisição de Compra - " + novosNegocios.nrNegocio);
                    setEmailAttachment(`REQUISICAO_COMPRA_${novosNegocios.nrNegocio}.pdf`);
                    setEmailBody(emailTemplates.requisicaoCompra);
                }
                setSelectedNovosNegocios(novosNegocios);

            } else {
                setSelectedNovosNegocios(undefined);
            }
        }
        setOpenEmailModal(openState);
    }, [
        novosNegociosLista
    ]);

    const handleEmailSubmit = useCallback(async (form: EmailForm) => {
        if (selectedNovosNegocios) {
            try {
                setLoading(true);

                await sendEmailNovosNegociosUseCase
                    .execute(
                        selectedNovosNegocios.idNovosNegocios!,
                        form.subject,
                        form.sendTo,
                        form.body,
                        form.attachment,
                    );

                pushInfoNotification("Sucesso", "Email enviado com sucesso!");
                setOpenEmailModal(false);
            } catch (e: unknown) {
                pushErrorNotification('Erro', processError(e, 'Falha ao enviar email!'));
            } finally {
                setLoading(false);
            }
        }
    }, [
        selectedNovosNegocios,
        sendEmailNovosNegociosUseCase,
        pushErrorNotification,
        pushInfoNotification
    ]);

    //#endregion

    return {
        loading,
        saving,
        novosNegociosLista: novosNegociosLista ?? [],
        errorMessage,
        filters,
        pagination,
        openDeleteModal,
        ddlCliente: ddlCliente ?? [],
        selectedIdNovosNegocios,
        openReportNovosNegociosModal,
        openEmailModal,
        emailSendTo,
        emailSubject,
        emailAttachment,
        emailBody,
        ddlCentroCusto: ddlCentroCusto ?? [],
        reportViewerState,

        handleEmailSubmit,
        handleEmailOpenState,
        handleOpenReportNovosNegociosModal,
        handleReportNovosNegociosListaSubmit,
        handleNovosNegociosExportar,
        handleNewNovosNegocios,
        handleEditNovosNegocios,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleClearFilters,
        handleIdsClienteChange,
        handleCidadeChange,
        handleDataChange,
        handleStatusChange,
        handleNovosNegociosDeleteOpenState,
        handleConfirmarDeletarNovosNegocios,

    };
}



